import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { StepCard } from "@/components/UsageGuide/StepCard";
import { ProgressTimeline } from "@/components/UsageGuide/ProgressTimeline";
import { GuideSection } from "@/components/UsageGuide/GuideSection";
import { DocumentActionButtons } from "@/components/Documentation/DocumentActionButtons";
import { ArrowLeft, ArrowRight, Home, Users, UserCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function UsageGuide() {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState("user-login");
  const [activeTab, setActiveTab] = useState("user");

  const userSections = [
    { id: "user-login", title: "First Login", steps: 2, completed: false },
    { id: "user-dashboard", title: "Dashboard Overview", steps: 1, completed: false },
    { id: "user-bills", title: "Bills & Contracts", steps: 2, completed: false },
    { id: "user-topup", title: "Top-Up Process", steps: 4, completed: false },
    { id: "user-monitoring", title: "Monitoring", steps: 2, completed: false },
  ];

  const adminSections = [
    { id: "admin-onboarding", title: "Getting Started", steps: 5, completed: false },
    { id: "admin-users", title: "User Management", steps: 1, completed: false },
    { id: "admin-buildings", title: "Buildings", steps: 1, completed: false },
    { id: "admin-notifications", title: "Notifications", steps: 1, completed: false },
    { id: "admin-reports", title: "System Reports", steps: 1, completed: false },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setCurrentSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = activeTab === "user" ? userSections : adminSections;
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id),
      }));

      for (const { id, element } of sectionElements) {
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setCurrentSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <Button
              variant="ghost"
              size="default"
              onClick={() => navigate("/")}
              className="text-primary-foreground hover:bg-white/20 no-print"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Home
            </Button>
            
            <DocumentActionButtons 
              variant="gradient" 
              pageTitle="Growth Energy Application Usage Guide"
            />
          </div>
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Application Usage Guide</h1>
            <p className="text-lg opacity-90">
              Visual step-by-step guide for users and administrators
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={(value) => {
          setActiveTab(value);
          setCurrentSection(value === "user" ? "user-login" : "admin-onboarding");
        }} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6 mx-auto">
            <TabsTrigger value="user" className="flex items-center gap-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
              <Users className="w-4 h-4" />
              User Guide
            </TabsTrigger>
            <TabsTrigger value="admin" className="flex items-center gap-2 data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <UserCog className="w-4 h-4" />
              Admin Guide
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-8">
            {/* Sidebar Timeline */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              {activeTab === "user" ? (
                <ProgressTimeline
                  sections={userSections}
                  currentSection={currentSection}
                  onSectionClick={scrollToSection}
                />
              ) : (
                <ProgressTimeline
                  sections={adminSections}
                  currentSection={currentSection}
                  onSectionClick={scrollToSection}
                />
              )}
            </aside>

            {/* Content Area */}
            <main className="flex-1 max-w-4xl">
              {/* User Guide */}
              <TabsContent value="user" className="mt-0">
                <ScrollArea className="h-[calc(100vh-250px)]">
                  <GuideSection
                    id="user-login"
                    title="First Login"
                    description="Access your account and start managing your energy"
                  >
                    <StepCard
                      stepNumber={1}
                      title="Organization Validation"
                      description="Enter your organization ID to begin the login process"
                      screenshot={import.meta.env.VITE_APP_LOGO}
                      instructions={[
                        "Navigate to the Growth Energy platform",
                        "Enter your organization ID (e.g., 'fumbademo')",
                        "Click 'Continue to Login' to proceed",
                        "The system will validate your organization",
                      ]}
                      tips={[
                        "Organization ID is case-sensitive",
                        "Contact your admin if you don't have an organization ID",
                        "This step ensures you're accessing the correct organization's data",
                      ]}
                    />

                    <StepCard
                      stepNumber={2}
                      title="Enter Credentials"
                      description="Sign in with your email and password"
                      screenshot={import.meta.env.VITE_APP_LOGO}
                      instructions={[
                        "Enter your email address provided by your organization",
                        "Type your password securely",
                        "Click 'Sign In' to access your dashboard",
                        "Use 'Demo' or 'Admin Login' for quick access if available",
                      ]}
                      tips={[
                        "Enable browser password manager for convenience",
                        "Use 'Forgot Password?' link if you can't remember your credentials",
                        "First-time users may need to set a new password",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="user-dashboard"
                    title="Dashboard Overview"
                    description="Your central hub for energy monitoring"
                  >
                    <StepCard
                      stepNumber={3}
                      title="Understanding Your Dashboard"
                      description="View balance, consumption, notices, and quick actions"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Check your current balance (displayed in TSh and kWh)",
                        "View consumption chart showing daily/weekly usage trends",
                        "Read important notices from your energy provider",
                        "Access quick actions: See Bills, Top Up",
                        "Navigate using the sidebar menu for all features",
                      ]}
                      tips={[
                        "Balance updates in real-time after top-ups",
                        "Consumption chart helps identify usage patterns",
                        "Red balance means low credit - time to top up!",
                        "Notices include important announcements and alerts",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="user-bills"
                    title="Bills & Contracts"
                    description="Access billing documents and service contracts"
                  >
                    <StepCard
                      stepNumber={4}
                      title="View Your Bills"
                      description="Access billing history and download PDFs"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Bills' in the sidebar menu",
                        "Browse bills organized by month and year",
                        "Check payment status: Paid, Unpaid, or Overdue",
                        "Click 'Download PDF' to save bill copies",
                        "Use 'Pay Now' button for unpaid bills",
                      ]}
                      tips={[
                        "Download PDFs for tax and record purposes",
                        "Bills show consumption breakdown by period",
                        "Set up payment reminders to avoid late fees",
                      ]}
                    />

                    <StepCard
                      stepNumber={5}
                      title="Access Your Contract"
                      description="View service agreement with verification QR code"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Contracts' in the sidebar",
                        "Review contract terms, start date, and end date",
                        "Check contract number (format: GE-CON-XXXXXXXXX)",
                        "Scan QR code for quick verification",
                        "Download contract PDF for your records",
                      ]}
                      tips={[
                        "Contract includes contact information for support",
                        "Two-column layout shows terms and contact details",
                        "Keep a copy of your contract for reference",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="user-topup"
                    title="Top-Up Process"
                    description="Add credit to your account using mobile money"
                  >
                    <StepCard
                      stepNumber={6}
                      title="Select Amount"
                      description="Choose quick amount or enter custom value"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Top Up' from sidebar or dashboard",
                        "View your current balance at the top",
                        "Select quick amount (5,000 / 10,000 / 20,000 TSh)",
                        "Or enter custom amount (minimum 5,000 TSh)",
                        "Choose mobile money provider (M-Pesa, Tigo, Airtel)",
                        "Review transaction summary",
                        "Click 'Continue to Payment'",
                      ]}
                      tips={[
                        "Larger top-ups reduce payment frequency",
                        "Transaction fees may apply per provider",
                        "Balance updates immediately after payment",
                      ]}
                    />

                    <StepCard
                      stepNumber={7}
                      title="Complete Payment"
                      description="Secure checkout via Fincra gateway"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "You'll be redirected to Fincra secure page",
                        "Verify amount and organization name",
                        "Enter your mobile money phone number",
                        "Ensure sufficient balance in mobile money",
                        "Click 'Pay Now' to initiate",
                        "Receive payment prompt on your phone",
                        "Enter mobile money PIN to confirm",
                      ]}
                      tips={[
                        "Keep phone nearby for payment prompt",
                        "Page times out after 10 minutes",
                        "Don't close browser during payment",
                        "SSL encryption ensures security",
                      ]}
                    />

                    <StepCard
                      stepNumber={8}
                      title="Payment Processing"
                      description="Wait for confirmation from provider"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "See 'Processing...' status on screen",
                        "Verification takes 30 seconds to 2 minutes",
                        "Do not close browser or app",
                        "Page auto-updates when confirmed",
                        "If failed, retry option appears",
                      ]}
                      tips={[
                        "Ensure good network connectivity",
                        "Check mobile money balance if delayed",
                        "Failed payments are not charged",
                        "Contact support if charged but not reflected",
                      ]}
                    />

                    <StepCard
                      stepNumber={9}
                      title="Payment Success"
                      description="Confirmation and updated balance"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Success message with checkmark appears",
                        "New balance displayed prominently",
                        "Transaction reference provided",
                        "SMS confirmation from mobile money",
                        "Click 'Go to Dashboard' to continue",
                        "Verify in transaction history",
                      ]}
                      tips={[
                        "Save transaction reference for inquiries",
                        "Balance reflects across all pages instantly",
                        "Download receipt from transaction history",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="user-monitoring"
                    title="Monitoring Consumption"
                    description="Track usage and identify patterns"
                  >
                    <StepCard
                      stepNumber={10}
                      title="Consumption Evolution"
                      description="Time-series charts of energy usage"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Consumption Evolution' in sidebar",
                        "Select time period: Daily, Weekly, Monthly",
                        "View line chart showing kWh over time",
                        "Hover over points for exact values",
                        "Use date picker for specific periods",
                        "Compare to previous periods",
                        "Export data as CSV",
                      ]}
                      tips={[
                        "Look for spikes indicating unusual usage",
                        "Weekly view shows daily patterns",
                        "Monthly view helps budgeting",
                        "Set consumption goals to reduce usage",
                      ]}
                    />

                    <StepCard
                      stepNumber={11}
                      title="Appliance Insights"
                      description="Breakdown by appliance category"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Appliance Insights' in sidebar",
                        "View pie chart with category breakdown",
                        "See percentages and kWh per category",
                        "Categories: Lighting, Cooking, Refrigeration, etc.",
                        "Review AI-powered recommendations",
                        "Click categories for detailed view",
                        "Monitor baseline load (always-on devices)",
                      ]}
                      tips={[
                        "Refrigeration typically 20-30% of usage",
                        "Identify phantom loads from standby devices",
                        "Follow recommendations to optimize",
                        "Track changes after efficiency measures",
                      ]}
                    />
                  </GuideSection>
                </ScrollArea>
              </TabsContent>

              {/* Admin Guide */}
              <TabsContent value="admin" className="mt-0">
                <ScrollArea className="h-[calc(100vh-250px)]">
                  <GuideSection
                    id="admin-onboarding"
                    title="Getting Started (Onboarding)"
                    description="Set up your organization and bulk import clients"
                  >
                    <StepCard
                      stepNumber={1}
                      title="Organization Setup"
                      description="Configure organization details and branding"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Navigate to /onboarding (accessible without login)",
                        "Enter organization name and contact details",
                        "Upload organization logo (PNG/JPG, max 5MB) - REQUIRED",
                        "Fill in admin email for system notifications",
                        "Add phone number and physical address",
                        "Optionally add organization description",
                        "Click 'Next' to proceed to templates",
                      ]}
                      tips={[
                        "Logo appears on all generated documents",
                        "Admin email receives important notifications",
                        "Contact info visible to all clients",
                        "Choose a professional logo for branding",
                      ]}
                    />

                    <StepCard
                      stepNumber={2}
                      title="PDF Templates"
                      description="Upload custom or use default templates"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Choose custom upload or use Growth Energy defaults",
                        "Click 'Preview Default Contract' to see format",
                        "Click 'Preview Default Bill' to see format",
                        "Upload custom contract PDF if desired",
                        "Upload custom bill PDF if desired",
                        "Templates must include required placeholders",
                        "Click 'Next' to configure buildings",
                      ]}
                      tips={[
                        "Defaults follow EDF contract standards",
                        "Custom templates need system integration fields",
                        "Preview ensures templates meet requirements",
                        "Can update templates later in admin panel",
                      ]}
                    />

                    <StepCard
                      stepNumber={3}
                      title="Buildings Configuration"
                      description="Add buildings and specify apartments"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Add Building' to create entry",
                        "Enter unique building name (no duplicates)",
                        "Add physical address",
                        "Specify total apartment count",
                        "System auto-creates apartment records",
                        "Add more buildings as needed",
                        "Remove with trash icon if needed",
                        "Click 'Next' for client import",
                      ]}
                      tips={[
                        "Use descriptive names (Building A, North Tower)",
                        "Duplicate names trigger warnings",
                        "Apartments auto-generated from count",
                        "Can add more buildings later from admin panel",
                      ]}
                    />

                    <StepCard
                      stepNumber={4}
                      title="Bulk Client Import"
                      description="Import clients via CSV with validation"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Download CSV template for format reference",
                        "Prepare data: meter_id, apartment_number, building_name (required)",
                        "Optional: first_name, last_name, email, phone",
                        "Upload CSV file (drag or click)",
                        "Review validation results (errors highlighted)",
                        "Fix errors in file and re-upload if needed",
                        "Check import options: Welcome Emails, Auto Client Numbers, Create Accounts",
                        "Click 'Next' to review before import",
                      ]}
                      tips={[
                        "meter_id must be unique per client",
                        "building_name must match step 3 exactly",
                        "Email optional but needed for welcome emails",
                        "Client numbers: CLI-{ORG}-{COUNTER}",
                        "Validation catches errors before import",
                      ]}
                    />

                    <StepCard
                      stepNumber={5}
                      title="Review & Complete"
                      description="Final review and import with progress tracking"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Review summary: buildings, apartments, clients count",
                        "Expand 'View All Clients' for detailed list",
                        "Verify all information is correct",
                        "Click 'Complete Setup' to start import",
                        "Watch real-time progress screen",
                        "Wait for success confirmation",
                        "Click 'Go to Dashboard' to access admin panel",
                      ]}
                      tips={[
                        "Import may take minutes for large datasets",
                        "Welcome emails sent asynchronously",
                        "User accounts can login immediately",
                        "Verify in admin dashboard after completion",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="admin-users"
                    title="User Management"
                    description="Add, edit, and manage user accounts"
                  >
                    <StepCard
                      stepNumber={6}
                      title="Managing Users"
                      description="Full user account administration"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'User Management' in admin sidebar",
                        "View all users in searchable table",
                        "Click 'Add User' to create manually",
                        "Edit icon to modify user information",
                        "'Deactivate' to disable access (reversible)",
                        "Search by name, email, or client number",
                        "Filter by building, status, or apartment",
                        "Export user list to CSV for reporting",
                      ]}
                      tips={[
                        "Deactivated users can't login but data preserved",
                        "Bulk import available in onboarding",
                        "Meter IDs difficult to change once assigned",
                        "Regular audits ensure data accuracy",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="admin-buildings"
                    title="Buildings Management"
                    description="Manage properties and apartments"
                  >
                    <StepCard
                      stepNumber={7}
                      title="Managing Buildings"
                      description="Add and configure buildings post-onboarding"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Buildings' in admin sidebar",
                        "View all buildings with apartment counts",
                        "Click 'Add Building' for new properties",
                        "Edit existing building details",
                        "View apartments within each building",
                        "Manage apartment assignments",
                        "Update addresses and details",
                      ]}
                      tips={[
                        "Changes affect all associated users",
                        "Can add buildings after initial onboarding",
                        "Apartment sorting helps organization",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="admin-notifications"
                    title="Client Notifications"
                    description="Send targeted messages to users"
                  >
                    <StepCard
                      stepNumber={8}
                      title="Notify Clients"
                      description="Create and send notifications with templates"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'Notify Clients' in admin sidebar",
                        "Enter notification title and message",
                        "Select type: Info, Warning, Success, Error",
                        "Choose target: All buildings, specific building, or user",
                        "Enter specific client ID for personalized messages",
                        "Click 'Add' in templates to create reusable templates",
                        "Use quick templates for common messages",
                        "Click 'Send Client Notification'",
                      ]}
                      tips={[
                        "Templates save time for frequent messages",
                        "Building-specific for location announcements",
                        "User-specific for personal messages",
                        "Choose appropriate type for better UX",
                        "Avoid excessive notifications",
                      ]}
                    />
                  </GuideSection>

                  <GuideSection
                    id="admin-reports"
                    title="System Reports & Analytics"
                    description="View organization-wide metrics"
                  >
                    <StepCard
                      stepNumber={9}
                      title="System Reports"
                      description="Consumption, revenue, and performance analytics"
                      screenshot="/placeholder.svg"
                      instructions={[
                        "Click 'System Reports' in admin sidebar",
                        "View key metrics: total users, active users, revenue",
                        "Analyze consumption across all buildings",
                        "Review payment patterns and top-up trends",
                        "Generate custom reports for time periods",
                        "Export reports to PDF or CSV",
                        "Monitor system performance",
                        "Compare building-level performance",
                      ]}
                      tips={[
                        "Schedule automatic report generation",
                        "Use for capacity planning and forecasting",
                        "Identify under-performing buildings",
                        "Share reports with management",
                      ]}
                    />
                  </GuideSection>
                </ScrollArea>
              </TabsContent>
            </main>
          </div>

          {/* Navigation Footer */}
          <div className="flex justify-center items-center pt-8 border-t mt-8">
            <Button
              onClick={() => navigate("/")}
            >
              <Home className="h-4 w-4 mr-2" />
              Go to Home
            </Button>
          </div>
        </Tabs>
      </div>

      {/* Back to Top Button */}
      <Button
        className="fixed bottom-8 right-8 rounded-full shadow-lg"
        size="icon"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowRight className="h-4 w-4 rotate-[-90deg]" />
      </Button>
    </div>
  );
}
